import React, { useState, useEffect, useCallback } from 'react';
import Header from '../components/Header';
import './VoterLookup.css';

function VoterLookup() {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

  useEffect(() => {
    document.title = 'Voter Lookup';
  }, []);

  const calculateAge = (birthday) => {
    if (!birthday) return '';
    const [month, day, year] = birthday.split('/').map(Number);
    const birthDate = new Date(year, month - 1, day);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const searchVoters = useCallback(async (query) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/search?q=${encodeURIComponent(query)}`);
      if (!response.ok) {
        throw new Error('Failed to fetch results');
      }
      const data = await response.json();
      setResults(data.results);
      setTotalCount(data.totalCount);
    } catch (err) {
      setError('Unable to connect to server');
      setResults([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim().length >= 1) {
        searchVoters(searchQuery);
      } else {
        setResults([]);
        setTotalCount(0);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchVoters]);

  return (
    <div className="voter-lookup-page">
      <Header title="Brookline Voter Lookup" />

      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="Search by name or address..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoFocus
        />

        {loading && <div className="loading">Searching...</div>}
        {error && <div className="error">{error}</div>}
      </div>

      <div className="results-container">
        {results.length > 0 && (
          <div className="results-count">
            {totalCount > 50
              ? `Showing ${results.length} of ${totalCount} results`
              : `${totalCount} result${totalCount !== 1 ? 's' : ''}`}
          </div>
        )}

        {results.map((voter) => {
          const age = calculateAge(voter.birthday);
          return (
            <div key={voter.id} className="voter-card">
              <div className="voter-name">
                {voter.firstName} {voter.lastName}
              </div>
              <div className="voter-details">
                <div className="detail-row">
                  <span className="detail-label">Birthday:</span>
                  <span className="detail-value">
                    {voter.birthday}{age ? ` (${age} years)` : ''}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Address:</span>
                  <span className="detail-value address-lines">
                    {voter.streetAddress && <div>{voter.streetAddress}</div>}
                    {voter.zipCode && <div>{voter.zipCode}</div>}
                  </span>
                </div>
                {voter.precinct && (
                  <div className="detail-row">
                    <span className="detail-label">Precinct:</span>
                    <span className="detail-value">{voter.precinct}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {searchQuery && !loading && results.length === 0 && (
          <div className="no-results">No voters found matching "{searchQuery}"</div>
        )}
      </div>
    </div>
  );
}

export default VoterLookup;
